/**
 * Bundles & Kits — the small pieces more than one page uses: the menu's theme list, the photo
 * collage (a bundle has no photo of its own; it shows its parts, 2×2), the bundle card and the
 * "Save with a bundle" block on a part's product page. The pages themselves are in Bundles.tsx,
 * loaded only when someone opens one.
 */
import { useEffect, useState } from "react";
import type { BundleCard, BundlesResponse } from "./types";
import { Go, type GoFn } from "./route";
import { get, rands } from "./shared";
import { Pic } from "./img";

/** One fetch of /shop/bundles per page load, shared by the menu and the page (edge-cached anyway). */
let bundlesOnce: Promise<BundlesResponse> | null = null;
export function loadBundles(): Promise<BundlesResponse> {
  bundlesOnce ??= get<BundlesResponse>("/shop/bundles").catch((e) => {
    bundlesOnce = null;
    throw e;
  });
  return bundlesOnce;
}

/** The themes that have a bundle in stock — the menu's sub-links. Empty until (and unless) loaded. */
export function useBundleThemes(): Array<{ theme: string; slug: string }> {
  const [themes, setThemes] = useState<Array<{ theme: string; slug: string }>>([]);
  useEffect(() => {
    let live = true;
    loadBundles()
      .then((d) => { if (live) setThemes(d.themes.map((t) => ({ theme: t.theme, slug: t.slug }))); })
      .catch(() => undefined);
    return () => { live = false; };
  }, []);
  return themes;
}

/** Up to four part photos in a 2×2 grid; one photo fills the square. */
export function BundleCollage({ images, alt, eager, sizes }: { images: string[]; alt: string; eager?: boolean; sizes: string }) {
  const shown = images.slice(0, 4);
  if (shown.length === 0) return <div className="skel fill" />;
  return (
    <div className={`collage collage-${shown.length}`} role="img" aria-label={alt}>
      {shown.map((src, i) => <Pic key={src} src={src} alt="" sizes={sizes} eager={eager && i === 0} width={300} height={300} />)}
    </div>
  );
}

/** "R1 840 · was R2 049,97 · save R209,97" as three pieces the card and the page both lay out. */
export function BundlePrice({ b, large }: { b: BundleCard; large?: boolean }) {
  return (
    <div className={`card-price money${large ? " bundle-price-lg" : ""}`}>
      <b className="sale">{rands(b.priceCents)}</b>
      <s>was {rands(b.partsCents)}</s>
      <span className="bundle-save">save {rands(b.savingCents)}</span>
    </div>
  );
}

export function BundleCardView({ b, go, i }: { b: BundleCard; go: GoFn; i: number }) {
  return (
    <article className="card">
      <Go to={{ name: "bundle", slug: b.slug }} go={go} className="card-link">
        <div className="card-media">
          <BundleCollage images={b.images} alt={b.name} eager={i < 2} sizes="(min-width: 1100px) 11vw, 23vw" />
          <span className="save-flag money">Save {rands(b.savingCents)}</span>
        </div>
        <div className="card-brand">{b.kind === "kit" ? "Kit" : "Bundle"} · {b.parts.length} items</div>
        <h3>{b.name}</h3>
        {b.pitch && <p className="bundle-pitch">{b.pitch}</p>}
        <BundlePrice b={b} />
        {b.onlyLeft !== null ? (
          <span className="stock low"><i aria-hidden="true" />Only {b.onlyLeft} left</span>
        ) : (
          <span className="stock"><i aria-hidden="true" />In stock</span>
        )}
      </Go>
    </article>
  );
}

/**
 * "Save with a bundle" on a part's product page: up to three in-stock bundles that hold it. Never
 * blocks the page; nothing to offer (or a failed read) renders nothing.
 */
export function SaveWithBundle({ handle, go }: { handle: string; go: GoFn }) {
  const [bundles, setBundles] = useState<BundleCard[]>([]);
  useEffect(() => {
    const ac = new AbortController();
    setBundles([]);
    get<{ bundles?: BundleCard[] }>(`/shop/product/${encodeURIComponent(handle)}/bundles`, ac.signal)
      .then((d) => setBundles(d.bundles ?? []))
      .catch(() => undefined);
    return () => ac.abort();
  }, [handle]);
  if (bundles.length === 0) return null;
  return (
    <section className="bundle-xsell" aria-label="Save with a bundle">
      <h2>Save with a bundle</h2>
      <ul>
        {bundles.map((b) => (
          <li key={b.slug}>
            <Go to={{ name: "bundle", slug: b.slug }} go={go} className="bundle-xsell-row">
              <span className="bundle-xsell-img"><BundleCollage images={b.images} alt="" sizes="64px" /></span>
              <span className="bundle-xsell-t">
                <b>{b.name}</b>
                <span className="money">{rands(b.priceCents)} · save {rands(b.savingCents)}</span>
              </span>
            </Go>
          </li>
        ))}
      </ul>
    </section>
  );
}
